import { Icon, IconProps, Link, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { useGlobalState } from '../../state/state';
import {
  getAmountDue,
  getCurrentMonthlyPaymentDetails,
  isAutopayBillPlan,
  isOnAnnualAutopayBillPlan,
  isOnMonthlyBillPlan,
} from '../../state/helpers';
import { useHistory } from 'react-router';
import { autopayLink } from '../../utils/generateURL';
import { navigateToMembershipBillPay } from '../../utils/navigateToMembershipBillPay';
import { UtagHelper, UtagType } from '../../utils/UtagHelper';

export const CardItems = () => {
  const history = useHistory();

  const [membershipDetails] = useGlobalState('memberDetails');
  const [paymentInfo] = useGlobalState('paymentInfo');
  const [priceCart] = useGlobalState('priceCart');
  const [tags] = useGlobalState('tags');

  const { currentBillPlan, isAutopayRequired } = membershipDetails;
  const isOnAutopay = isAutopayBillPlan(currentBillPlan!);
  const isOnAnnualAutopay = isOnAnnualAutopayBillPlan(currentBillPlan!);
  const isMonthlyBillPlan = isOnMonthlyBillPlan(currentBillPlan!);
  const amountDue = getAmountDue(membershipDetails, paymentInfo.monthlyPaymentData, priceCart);
  const hasPaymentDue = amountDue > 0;
  const enrollAutopayText = hasPaymentDue ? 'Pay & enroll in Auto Pay' : 'Enroll in Auto Pay';
  const displayAutoPayLink = !isOnAutopay && !isAutopayRequired

  const getPaymentNotificationProps = () => {
    if (isOnAnnualAutopay && hasPaymentDue) {
      return {
        message: 'Your membership will renew automatically',
        iconName: 'arrows-rotate-regular',
        className: 'autopay-message',
        iconColor: '#0C72DF',
      };
    } else if (isMonthlyBillPlan) {
      const currentMonthlyPaymentDetails = getCurrentMonthlyPaymentDetails(paymentInfo.monthlyPaymentData);
      const { monthlyPaymentPastDue: isPastDue, monthlyPaymentDueDate: dueDate } = currentMonthlyPaymentDetails;

      if (hasPaymentDue) {
        if (isPastDue) {
          return {
            message: 'Review your Auto Pay method',
            iconName: 'rotate-exclamation-regular',
            className: 'auto-pay-method-review',
            color: '#B53217',
          };
        } else {
          return {
            message: `Payment is scheduled for ${dueDate}`,
            iconName: 'arrows-rotate-regular',
            className: 'auto-scheduled-payment',
            iconColor: '#0C72DF',
          };
        }
      }
    }
  };

  const getPaymentNotification = () => {
    const notificationProps = getPaymentNotificationProps();
    return (
      notificationProps && (
        <Stack direction={'row'} gap={'4px'} alignItems={'center'}>
          <Icon
            iconName={notificationProps.iconName as IconProps['iconName']}
            className={`${notificationProps.className}-icon`}
            sx={{ fontSize: '1.2Rem', color: notificationProps.iconColor ?? notificationProps.color }}
          />
          <Typography
            variant="b2Regular"
            className={`${notificationProps.className}-message`}
            sx={notificationProps.color ? { color: notificationProps.color } : undefined}
          >
            {notificationProps.message}
          </Typography>
        </Stack>
      )
    );
  };

  const handleAutopayLinkClick = async () => {
    UtagHelper.sendTag(UtagType.link, tags, '', {
      cta: `my account:manage my membership-${hasPaymentDue ? 'pay and enroll in auto pay' : 'enroll in auto pay'}`,
    });
    hasPaymentDue
      ? await navigateToMembershipBillPay(membershipDetails, tags, history, true)
      : setTimeout(() => {
          window.location.assign(autopayLink(membershipDetails?.sessionId || ''));
        }, 500);
  };

  return (
    <Stack sx={{ padding: '24px 16px', gap: '8px', backgroundColor: '#F7F8FA' }}>
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        gap={'8px'}
        className="card-items-container autopay-container"
      >
        <Typography className="auto-pay-description" variant="b1Regular">
          Auto Pay:
        </Typography>
        <Typography variant="b1Bold" className="auto-pay-info" marginBottom={'0 !important'} marginRight={'0.5rem'}>
          {isOnAutopay ? 'Enrolled' : 'Not enrolled'}
        </Typography>
      </Stack>
      {displayAutoPayLink && (
        <Link
          className="enroll-autopay-link"
          sx={{
            alignSelf: 'end',
            marginRight: '8px !important',
            ':hover': {
              cursor: 'pointer',
            },
          }}
          onClick={handleAutopayLinkClick}
        >
          <Typography variant="b1Medium" color="#0156A3" sx={{ textDecorationColor: '#0156A3' }}>
            {enrollAutopayText}
          </Typography>
        </Link>
      )}

      {getPaymentNotification()}
    </Stack>
  );
};
